/**
 * Group List View
 * Displays distribution groups with pagination, search, and filtering
 */
import React, { useState, useMemo } from 'react';
import {
  MagnifyingGlassIcon,
  PlusIcon,
  FunnelIcon,
  ChevronUpIcon,
  ChevronDownIcon,
  UserGroupIcon,
  EllipsisVerticalIcon,
  PencilIcon,
  TrashIcon,
  UsersIcon,
  CheckCircleIcon,
  XCircleIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
} from '@heroicons/react/24/outline';
import { useDistributionGroupList, useDeleteDistributionGroup } from '../../hooks/useDistributionGroups';
import { useDomains } from '../../hooks/useMailboxes';
import type { DistributionGroup, GroupType, GroupFilters } from '../../types/admin.types';

interface GroupListViewProps {
  onCreateGroup: () => void;
  onEditGroup: (group: DistributionGroup) => void;
  onManageMembers: (group: DistributionGroup) => void;
}

type SortField = 'display_name' | 'email' | 'created_at' | 'member_count';
type SortOrder = 'asc' | 'desc';

export const GroupListView: React.FC<GroupListViewProps> = ({
  onCreateGroup,
  onEditGroup,
  onManageMembers,
}) => {
  // Filter state
  const [search, setSearch] = useState('');
  const [groupTypeFilter, setGroupTypeFilter] = useState<GroupType | 'all'>('all');
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive'>('all');
  const [domainFilter, setDomainFilter] = useState<string>('');
  const [showFilters, setShowFilters] = useState(false);

  // Sorting state
  const [sortBy, setSortBy] = useState<SortField>('display_name');
  const [sortOrder, setSortOrder] = useState<SortOrder>('asc');

  // Pagination state
  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);

  // Selected groups for bulk actions
  const [selectedGroups, setSelectedGroups] = useState<Set<string>>(new Set());

  // Delete confirmation
  const [deleteConfirm, setDeleteConfirm] = useState<DistributionGroup | null>(null);

  // Menu state
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);

  // Build query params
  const queryParams: GroupFilters = useMemo(
    () => ({
      search: search || undefined,
      groupType: groupTypeFilter !== 'all' ? groupTypeFilter : undefined,
      isActive: statusFilter === 'all' ? undefined : statusFilter === 'active',
      domainId: domainFilter || undefined,
      sortBy,
      sortOrder,
      page,
      pageSize,
    }),
    [search, groupTypeFilter, statusFilter, domainFilter, sortBy, sortOrder, page, pageSize]
  );

  // Fetch data
  const { data, isLoading, error } = useDistributionGroupList(queryParams);
  const { data: domainsData } = useDomains();
  const deleteGroupMutation = useDeleteDistributionGroup();

  // Handlers
  const handleSort = (field: SortField) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('asc');
    }
    setPage(1);
  };

  const handleSelectAll = () => {
    if (selectedGroups.size === (data?.groups.length || 0)) {
      setSelectedGroups(new Set());
    } else {
      setSelectedGroups(new Set(data?.groups.map((g) => g.group_id) || []));
    }
  };

  const handleSelectGroup = (groupId: string) => {
    const newSelected = new Set(selectedGroups);
    if (newSelected.has(groupId)) {
      newSelected.delete(groupId);
    } else {
      newSelected.add(groupId);
    }
    setSelectedGroups(newSelected);
  };

  const handleDeleteGroup = async (group: DistributionGroup) => {
    try {
      await deleteGroupMutation.mutateAsync(group.group_id);
      setDeleteConfirm(null);
      setOpenMenuId(null);
    } catch (err) {
      console.error('Failed to delete group:', err);
    }
  };

  const clearFilters = () => {
    setSearch('');
    setGroupTypeFilter('all');
    setStatusFilter('all');
    setDomainFilter('');
    setPage(1);
  };

  // Sort icon component
  const SortIcon: React.FC<{ field: SortField }> = ({ field }) => {
    if (sortBy !== field) return null;
    return sortOrder === 'asc' ? (
      <ChevronUpIcon className="w-4 h-4 inline-block ml-1" />
    ) : (
      <ChevronDownIcon className="w-4 h-4 inline-block ml-1" />
    );
  };

  // Get group type badge
  const getGroupTypeBadge = (type: GroupType) => {
    const badges: Record<GroupType, { label: string; className: string }> = {
      distribution: {
        label: 'Distribution',
        className: 'bg-lex-accent-soft text-lex-accent',
      },
      security: {
        label: 'Security',
        className: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300',
      },
      mail_enabled_security: {
        label: 'Mail-Enabled Security',
        className: 'bg-lex-accent-soft text-lex-accent',
      },
    };
    const badge = badges[type];
    return (
      <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${badge.className}`}>
        {badge.label}
      </span>
    );
  };

  // Loading state
  if (isLoading && !data) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-lex-accent"></div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
        <p className="text-red-700 dark:text-red-300">
          Failed to load distribution groups. Please try again.
        </p>
      </div>
    );
  }

  const groups = data?.groups || [];
  const memberCounts = data?.memberCounts || {};
  const totalPages = data?.totalPages || 1;

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Distribution Groups</h2>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Manage email distribution lists and group membership
          </p>
        </div>
        <button
          onClick={onCreateGroup}
          className="inline-flex items-center px-4 py-2 bg-lex-accent text-lex-accent-contrast rounded-lg hover:bg-lex-accent-hover transition-colors"
        >
          <PlusIcon className="w-5 h-5 mr-2" />
          New Group
        </button>
      </div>

      {/* Search and Filters */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
        <div className="flex flex-col sm:flex-row gap-4">
          {/* Search */}
          <div className="flex-1 relative">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search groups..."
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-lex-accent focus:border-transparent"
            />
          </div>

          {/* Filter toggle */}
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`inline-flex items-center px-4 py-2 border rounded-lg transition-colors ${
              showFilters
                ? 'border-lex-accent text-lex-accent bg-lex-accent-soft'
                : 'border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
            }`}
          >
            <FunnelIcon className="w-5 h-5 mr-2" />
            Filters
          </button>
        </div>

        {/* Expanded Filters */}
        {showFilters && (
          <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700 grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Group Type
              </label>
              <select
                value={groupTypeFilter}
                onChange={(e) => {
                  setGroupTypeFilter(e.target.value as GroupType | 'all');
                  setPage(1);
                }}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              >
                <option value="all">All Types</option>
                <option value="distribution">Distribution</option>
                <option value="security">Security</option>
                <option value="mail_enabled_security">Mail-Enabled Security</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Status
              </label>
              <select
                value={statusFilter}
                onChange={(e) => {
                  setStatusFilter(e.target.value as 'all' | 'active' | 'inactive');
                  setPage(1);
                }}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              >
                <option value="all">All Status</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Domain
              </label>
              <select
                value={domainFilter}
                onChange={(e) => {
                  setDomainFilter(e.target.value);
                  setPage(1);
                }}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              >
                <option value="">All Domains</option>
                {domainsData?.map((domain) => (
                  <option key={domain.domain_id} value={domain.domain_id}>
                    {domain.domain}
                  </option>
                ))}
              </select>
            </div>

            <div className="sm:col-span-3 flex justify-end">
              <button
                onClick={clearFilters}
                className="text-sm text-lex-accent hover:underline"
              >
                Clear all filters
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Groups Table */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 dark:bg-gray-900/50">
              <tr>
                <th className="w-12 px-4 py-3">
                  <input
                    type="checkbox"
                    checked={selectedGroups.size === groups.length && groups.length > 0}
                    onChange={handleSelectAll}
                    className="rounded border-gray-300 dark:border-gray-600 text-lex-accent focus:ring-lex-accent"
                  />
                </th>
                <th
                  onClick={() => handleSort('display_name')}
                  className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:text-gray-700 dark:hover:text-gray-200"
                >
                  Group
                  <SortIcon field="display_name" />
                </th>
                <th
                  onClick={() => handleSort('email')}
                  className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:text-gray-700 dark:hover:text-gray-200"
                >
                  Email
                  <SortIcon field="email" />
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Type
                </th>
                <th
                  onClick={() => handleSort('member_count')}
                  className="px-4 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:text-gray-700 dark:hover:text-gray-200"
                >
                  Members
                  <SortIcon field="member_count" />
                </th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {groups.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-4 py-12 text-center">
                    <UserGroupIcon className="mx-auto h-12 w-12 text-gray-400" />
                    <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">
                      No distribution groups
                    </h3>
                    <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                      Get started by creating a new distribution group.
                    </p>
                    <button
                      onClick={onCreateGroup}
                      className="mt-4 inline-flex items-center px-4 py-2 bg-lex-accent text-lex-accent-contrast rounded-lg hover:bg-lex-accent-hover"
                    >
                      <PlusIcon className="w-5 h-5 mr-2" />
                      New Group
                    </button>
                  </td>
                </tr>
              ) : (
                groups.map((group) => (
                  <tr
                    key={group.group_id}
                    className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                  >
                    <td className="px-4 py-4">
                      <input
                        type="checkbox"
                        checked={selectedGroups.has(group.group_id)}
                        onChange={() => handleSelectGroup(group.group_id)}
                        className="rounded border-gray-300 dark:border-gray-600 text-lex-accent focus:ring-lex-accent"
                      />
                    </td>
                    <td className="px-4 py-4">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-10 w-10 rounded-lg bg-lex-accent-soft flex items-center justify-center">
                          <UserGroupIcon className="h-5 w-5 text-lex-accent" />
                        </div>
                        <div className="ml-3">
                          <p className="text-sm font-medium text-gray-900 dark:text-white">
                            {group.display_name}
                          </p>
                          {group.description && (
                            <p className="text-xs text-gray-500 dark:text-gray-400 truncate max-w-xs">
                              {group.description}
                            </p>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-4">
                      <span className="text-sm text-gray-900 dark:text-white">{group.email}</span>
                    </td>
                    <td className="px-4 py-4">{getGroupTypeBadge(group.group_type)}</td>
                    <td className="px-4 py-4 text-center">
                      <button
                        onClick={() => onManageMembers(group)}
                        className="inline-flex items-center px-2 py-1 text-sm text-lex-accent hover:bg-lex-state-hover rounded"
                      >
                        <UsersIcon className="w-4 h-4 mr-1" />
                        {memberCounts[group.group_id] || 0}
                      </button>
                    </td>
                    <td className="px-4 py-4 text-center">
                      {group.is_active ? (
                        <span className="inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-full bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300">
                          <CheckCircleIcon className="w-3 h-3 mr-1" />
                          Active
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-full bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300">
                          <XCircleIcon className="w-3 h-3 mr-1" />
                          Inactive
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-4 text-right">
                      <div className="relative">
                        <button
                          onClick={() => setOpenMenuId(openMenuId === group.group_id ? null : group.group_id)}
                          className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700"
                        >
                          <EllipsisVerticalIcon className="w-5 h-5" />
                        </button>
                        {openMenuId === group.group_id && (
                          <>
                            <div
                              className="fixed inset-0 z-10"
                              onClick={() => setOpenMenuId(null)}
                            />
                            <div className="absolute right-0 mt-2 w-48 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 z-20">
                              <button
                                onClick={() => {
                                  onEditGroup(group);
                                  setOpenMenuId(null);
                                }}
                                className="w-full flex items-center px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700"
                              >
                                <PencilIcon className="w-4 h-4 mr-3" />
                                Edit Group
                              </button>
                              <button
                                onClick={() => {
                                  onManageMembers(group);
                                  setOpenMenuId(null);
                                }}
                                className="w-full flex items-center px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700"
                              >
                                <UsersIcon className="w-4 h-4 mr-3" />
                                Manage Members
                              </button>
                              <div className="border-t border-gray-200 dark:border-gray-700" />
                              <button
                                onClick={() => {
                                  setDeleteConfirm(group);
                                  setOpenMenuId(null);
                                }}
                                className="w-full flex items-center px-4 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20"
                              >
                                <TrashIcon className="w-4 h-4 mr-3" />
                                Delete Group
                              </button>
                            </div>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {groups.length > 0 && (
          <div className="px-4 py-3 border-t border-gray-200 dark:border-gray-700 flex items-center justify-between">
            <div className="text-sm text-gray-500 dark:text-gray-400">
              Showing {(page - 1) * pageSize + 1} to {Math.min(page * pageSize, data?.total || 0)} of{' '}
              {data?.total || 0} groups
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => setPage(Math.max(1, page - 1))}
                disabled={page === 1}
                className="p-2 rounded-lg border border-gray-300 dark:border-gray-600 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 dark:hover:bg-gray-700"
              >
                <ChevronLeftIcon className="w-5 h-5" />
              </button>
              <span className="text-sm text-gray-700 dark:text-gray-300">
                Page {page} of {totalPages}
              </span>
              <button
                onClick={() => setPage(Math.min(totalPages, page + 1))}
                disabled={page === totalPages}
                className="p-2 rounded-lg border border-gray-300 dark:border-gray-600 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 dark:hover:bg-gray-700"
              >
                <ChevronRightIcon className="w-5 h-5" />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full mx-4 p-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
              Delete Distribution Group
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
              Are you sure you want to delete <strong>{deleteConfirm.display_name}</strong>? This
              action cannot be undone and all members will be removed from the group.
            </p>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setDeleteConfirm(null)}
                className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
              >
                Cancel
              </button>
              <button
                onClick={() => handleDeleteGroup(deleteConfirm)}
                disabled={deleteGroupMutation.isPending}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
              >
                {deleteGroupMutation.isPending ? 'Deleting...' : 'Delete Group'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GroupListView;
